import { Component, AfterViewInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ViewChild } from '@angular/core';
import { Input, Output, EventEmitter} from '@angular/core';

import { VisibleDirective } from '../../directives/upp-visible';

/**
 * @component UppVisibleControlComponent
 * @description
 * A wrapper component that utilizes the `VisibleDirective` to detect and manage visibility changes.
 * It optimizes performance by detaching and reattaching Angular's change detection mechanism
 * based on the component's visibility.
 * 
 * @example
 * ```html
 * <upp-visible-control (visibilityChange)="onVisibilityChange($event)">
 *   <p>Content inside this component will only update when visible.</p>
 * </upp-visible-control>
 * ```
 * 
 * ```typescript
 * onVisibilityChange(isVisible: boolean) {
 *   console.log('Component is visible:', isVisible);
 * }
 * ```
 */
@Component({
    selector: 'upp-visible-control',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-visible.html',
    styleUrls: ['./upp-visible.scss'],
    host: {
        '[class.--fill]': "mode === 'fill'",
        '[class.--content]': "mode === 'content'",
    },
})
export class UppVisibleControlComponent implements AfterViewInit, OnDestroy {

    private _documentVisibleHandler: (() => void) | null = null;

    /**
     * @input mode
     * @description
     * Layout participation of the wrapper. `'fill'` (default) makes the host and its
     * inner observed `<span>` a vertical flex chain with `height: 100%` that stretches
     * to fill the parent (flex or block with explicit height), so consumers no longer
     * need to penetrate the internal `<span>` with `::ng-deep`.
     * `'content'` keeps the wrapper neutral so the projected content sizes to itself
     * (thumbs, grid cells, demos).
     */
    @Input() mode: 'content' | 'fill' = 'fill';

    /**
     * @output visibilityChange
     * @description
     * Emits a boolean value whenever the component's visibility changes.
     * `true` if the component is visible, `false` otherwise.
     */    
    @Output() visibilityChange: EventEmitter<boolean> = new EventEmitter();

    /**
     * @constructor
     * @param change A reference to Angular's ChangeDetectorRef, used to manually control change detection.
     */    
    constructor(private change: ChangeDetectorRef){
        // nothing to do
    }

    @ViewChild(VisibleDirective) _directive!: VisibleDirective | undefined;

    /**
     * @method ngAfterViewInit
     * @description
     * Lifecycle hook that runs after Angular has initialized the view.
     * It checks if the component is initially visible and detaches change detection if it is not.
     */    
    ngAfterViewInit() {
        if (this._directive && !this._directive.isVisible) {
            this.change.detach();
        }

        this._documentVisibleHandler = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }

            this.change.reattach();
            this.change.detectChanges();
        };
        
        document.addEventListener('visibilitychange', this._documentVisibleHandler);
    }

    ngOnDestroy(): void {
        if (this._documentVisibleHandler) {
            document.removeEventListener('visibilitychange', this._documentVisibleHandler);
            this._documentVisibleHandler = null;
        }
    }

    /**
     * @method onVisibilityChange
     * @description
     * Handles visibility changes detected by the `VisibleDirective`.
     * Detaches Angular's change detection when the component is hidden and reattaches it when visible.
     * @param {boolean} isVisible - The new visibility state of the component.
     */    
    onVisibilityChange(isVisible: boolean) {
        this.visibilityChange.emit(isVisible);            

        if (!isVisible) {   // no not compute changes if not visible
            this.change.detach();
        } 
        else {              // detect changes whenever it becomes visible
            this.change.reattach();
            this.change.detectChanges()
        }
    }
}